import {
  RecommendationAttemptStatusDto,
  RecommendationDto,
  RecommendationSourceDto,
  RemediationActivityTypeDto,
} from '../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { RemediationBaseState } from '../hocs/with-remediation-base/withRemediationBase.hoc';
import {
  getAssessmentGoalTotalsCombined,
  getAttemptsFromRecommendation,
  getIncompleteRecommendationsForIncompleteGoals,
  isGoalComplete,
  sortRecommendations
} from './remediation.utilities';

/**
 * Whether at least one Review (REVIEW_BY_TAXON) assessment goal is fully complete.
 * Used to gate when a second recommendation is offered on the post-player screen —
 * we don't show secondary recs until the student has finished at least one Review goal.
 * @internal Exported for testing
 */
export const hasAnyCompletedReviewGoal = (baseState: RemediationBaseState): boolean => {
  const assessmentGoals = baseState?.eolsAssessment?.assessmentGoals;
  if (!assessmentGoals || !assessmentGoals.length) {
    return false;
  }
  return assessmentGoals.some((goal) => {
    if (goal.type !== RemediationActivityTypeDto.REVIEW_BY_TAXON) {
      return false;
    }
    const metrics = getAssessmentGoalTotalsCombined({ assessmentGoal: goal, baseState });
    return isGoalComplete(metrics);
  });
};

/**
 * Minimum number of sources an Apply rec must share with the already-completed Review
 * recs for it to be eligible as a second recommendation.
 */
export const APPLY_REC_REVIEW_SOURCE_OVERLAP_THRESHOLD = 2;

/**
 * Return every RecommendationSourceDto belonging to the given recommendation.
 * @internal Exported for testing
 */
export const getSourcesForRecommendation = (
  recommendationId: number,
  allSources: RecommendationSourceDto[]
): RecommendationSourceDto[] => {
  if (!allSources || !allSources.length) {
    return [];
  }
  return allSources.filter((source) => source.recommendationId === recommendationId);
};

/**
 * Number of distinct sourceIds the target recommendation shares with the combined
 * sources of the given pool of recommendations. Set-intersection semantics: duplicate
 * sourceIds on either side are collapsed before counting.
 * @internal Exported for testing
 */
export const countRecSourceOverlapWithPool = (
  targetRecId: number,
  poolRecIds: number[],
  allSources: RecommendationSourceDto[]
): number => {
  const targetSources = getSourcesForRecommendation(targetRecId, allSources);

  if (!targetSources.length || !poolRecIds.length) {
    return 0;
  }

  const targetSourceIds = new Set(targetSources.map((source) => source.sourceId));
  const poolSourceIds = new Set(poolRecIds.flatMap((recId) => getSourcesForRecommendation(recId, allSources).map((source) => source.sourceId)));

  let overlap = 0;
  targetSourceIds.forEach((sourceId) => {
    if (poolSourceIds.has(sourceId)) {
      overlap += 1;
    }
  });
  return overlap;
};

export type RecWithSourceOverlap = {
  recommendation: RecommendationDto;
  sourceOverlap: number;
};

/**
 * Rank a list of candidate recommendations by how many of their sources overlap with
 * the combined sources of the given pool of recommendations.
 *
 * @param candidateRecs - Recs to rank
 * @param poolRecs - Recs whose sources form the comparison pool
 * @param allSources - All RecommendationSourceDto rows in scope
 * @returns Each candidate paired with its overlap count, sorted by overlap descending
 */
export const rankRecsBySourceOverlap = (
  candidateRecs: RecommendationDto[],
  poolRecs: RecommendationDto[],
  allSources: RecommendationSourceDto[]
): RecWithSourceOverlap[] => {
  if (!candidateRecs.length || !poolRecs.length || !allSources.length) {
    return [];
  }

  const poolRecIds = poolRecs.map((rec) => rec.id);

  const ranked: RecWithSourceOverlap[] = candidateRecs.map((rec) => ({
    recommendation: rec,
    sourceOverlap: countRecSourceOverlapWithPool(rec.id, poolRecIds, allSources),
  }));

  return ranked.sort((a, b) => b.sourceOverlap - a.sourceOverlap);
};

const getNextRecommendationByType = (
  baseState: RemediationBaseState,
  type: RemediationActivityTypeDto,
  skipRecommendationIds: number[] = []
): RecommendationDto | null => {
  if (!baseState || !baseState.recommendations || !baseState.eolsAssessment) {
    return null;
  }

  const incomplete = getIncompleteRecommendationsForIncompleteGoals(
    baseState,
    skipRecommendationIds
  ).filter((rec) => rec.assessmentGoalType === type);

  if (!incomplete.length) {
    return null;
  }

  return sortRecommendations(incomplete, baseState.eolsAssessment.assessmentGoals)[0];
};

/**
 * Pick the second recommendation to show alongside the primary "next" recommendation
 * on the post-player screen.
 *
 * **Gate:** at least one Review (REVIEW_BY_TAXON) assessment goal must be fully
 * complete. Until then, no second recommendation is offered (returns `null`).
 *
 * Every candidate must differ in activity type from `firstRecommendation` — no
 * branch may return a rec whose `assessmentGoalType` matches the first.
 *
 * Branch order:
 *   1. **Apply rec matched to completed Reviews** — if any Apply goal is still
 *      incomplete and any Review is already complete, surface the Apply rec whose
 *      sources overlap most with the completed Reviews, *iff* the overlap meets
 *      `APPLY_REC_REVIEW_SOURCE_OVERLAP_THRESHOLD`.
 *   2. **Apply → Assess** — if the first rec was Apply, surface the next Assess.
 *   3. **Fallback** — sorted incomplete recommendations (Review → Apply → Assess by
 *      activity type, then goal order, then displayOrder).
 *
 * Returns `null` when no suitable second recommendation exists.
 */
export const getSecondRecommendation = (
  baseState: RemediationBaseState,
  firstRecommendation: RecommendationDto
): RecommendationDto | null => {
  if (!baseState || !baseState.recommendations || !baseState.eolsAssessment) {
    return null;
  }

  if (!firstRecommendation) {
    return null;
  }

  if (!hasAnyCompletedReviewGoal(baseState)) {
    return null;
  }

  const { recommendations, recommendationAttempts, recommendationSources } = baseState;

  const sameTypeRecIds = (recommendations || [])
    .filter((rec) => rec.assessmentGoalType === firstRecommendation.assessmentGoalType)
    .map((rec) => rec.id);
  const skipIds = Array.from(new Set([firstRecommendation.id, ...sameTypeRecIds]));

  // Candidate pool excludes recs whose goal is already complete (a goal can be
  // complete by its metrics while individual recs under it remain incomplete).
  // Branch 1: case study with sufficient shared sources to completed Reviews
  const incompleteRecs = getIncompleteRecommendationsForIncompleteGoals(
    baseState,
    skipIds
  );
  const hasIncompleteApply = incompleteRecs.some((rec) => rec.assessmentGoalType === RemediationActivityTypeDto.APPLY_BY_CONTENT_TYPE);

  if (hasIncompleteApply && recommendationSources && recommendationSources.length > 0) {
    const completedReviewRecs = (recommendations || []).filter((rec) => {
      if (rec.assessmentGoalType !== RemediationActivityTypeDto.REVIEW_BY_TAXON) {
        return false;
      }
      const attempts = getAttemptsFromRecommendation(rec.id, recommendationAttempts);
      return attempts?.some((attempt) => attempt.status === RecommendationAttemptStatusDto.COMPLETED_PASSED);
    });

    if (completedReviewRecs.length > 0) {
      const availableApplyRecs = incompleteRecs.filter((rec) => rec.assessmentGoalType === RemediationActivityTypeDto.APPLY_BY_CONTENT_TYPE);
      const [topApplyRec] = rankRecsBySourceOverlap(availableApplyRecs, completedReviewRecs, recommendationSources);
      if (topApplyRec && topApplyRec.sourceOverlap >= APPLY_REC_REVIEW_SOURCE_OVERLAP_THRESHOLD) {
        return topApplyRec.recommendation;
      }
    }
  }

  // Branch 2: Apply → Assess
  if (firstRecommendation.assessmentGoalType === RemediationActivityTypeDto.APPLY_BY_CONTENT_TYPE) {
    const quizRec = getNextRecommendationByType(
      baseState,
      RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE,
      skipIds
    );
    if (quizRec) {
      return quizRec;
    }
  }

  // Branch 3: sorted fallback
  if (!incompleteRecs.length) {
    return null;
  }

  return sortRecommendations(incompleteRecs, baseState.eolsAssessment.assessmentGoals)[0] || null;
};
